// components/OrderDetail.js
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import "../../styles/orderdetail.css";
import Navbar from "../common/Navbar.jsx";
import Footer from "../common/Footer.jsx";

const OrderDetail = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchOrderDetails();
  }, [orderId]);

  const fetchOrderDetails = async () => {
    const token = localStorage.getItem("clientToken");
    if (!token) {
      navigate("/login");
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/orders/${orderId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch order details");
      }

      const data = await response.json();
      setOrder(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "pending":
        return "#ffbb00e1";
      case "processing":
        return "#2196f3";
      case "ready":
        return "#4caf50";
      case "payed":
        return "#9c27b0";
      case "delivered":
        return "#009688";
      case "cancelled":
        return "#f44336";
      default:
        return "#9e9e9e";
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "pending":
        return <i className="fa-solid fa-clock"></i>;
      case "processing":
        return <i className="fa-solid fa-gears"></i>;
      case "ready":
        return <i className="fa-solid fa-check"></i>;
      case "payed":
        return <i className="fa-solid fa-credit-card"></i>;
      case "delivered":
        return <i className="fa-solid fa-truck"></i>;
      case "cancelled":
        return <i className="fa-solid fa-ban"></i>;
      default:
        return <i className="fa-solid fa-file"></i>;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (isLoading) {
    return (
      <div>
        <Navbar />
        <div className="loading-container">
          <i className="fa-solid fa-spinner fa-spin"></i>
          <p>Loading order details...</p>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div>
        <Navbar />
        <div className="error-container">
          <i className="fa-solid fa-triangle-exclamation"></i>
          <h3>Order not found</h3>
          <p>{error || "The order you're looking for doesn't exist."}</p>
          <button onClick={() => navigate("/my-orders")} className="back-btn">
            Back to My Orders
          </button>
        </div>
      </div>
    );
  }
  const generatePDF = () => {
    if (!order) return;

    const doc = new jsPDF("p", "pt", "a4"); // portrait, points, A4
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    // ---------- COLORS ----------
    const primaryColor = [60, 63, 140]; // dark blue
    const lightColor = [238, 238, 238]; // light gray
    const accentColor = [0, 166, 216]; // cyan

    // Load background header image
    const headerImg = new Image();
    headerImg.src = "../images/bg8.jpg";

    // Load watermark logo
    const logoImg = new Image();
    logoImg.src = "../images/iblotec logo.png";

    headerImg.onload = () => {
      // ---------- HEADER FILLED WITH BACKGROUND IMAGE ----------
      const headerHeight = 120;
      const borderRadius = 50;

      // Draw rounded rect
      doc.setDrawColor(255, 255, 255);
      doc.setLineWidth(0);
      doc.roundedRect(
        0,
        0,
        pageWidth,
        headerHeight,
        borderRadius,
        borderRadius,
        "F"
      );

      // Clip the header image inside rounded rect
      doc.addImage(headerImg, "PNG", 0, 0, pageWidth, headerHeight);
      doc.addImage(logoImg, "PNG", 20, 65, 50, 50);

      // Company Name (over the header background)
      doc.setTextColor(...primaryColor);
      doc.setFontSize(22);
      doc.setFont("Nunito", "bold");
      doc.text("IBLOTEC MARKET", 80, 100);

      // Subtitle / Invoice
      doc.setFontSize(14);
      doc.setFont("Nunito", "normal");
      doc.text("Invoice", pageWidth - 80, 100, { align: "right" });

      // ---------- ORDER INFO ----------
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      doc.text(`Order Code: ${order.orderCode}`, 20, 180);
      doc.text(`Order Date: ${formatDate(order.createdAt)}`, 20, 200);
      doc.text(`Status: ${order.state?.toUpperCase()}`, 20, 220);

      // ---------- ORDER ITEMS TABLE ----------
      const tableColumn = [
        "Product",
        "Quantity",
        "Unit Price (Fbu)",
        "Total (Fbu)",
      ];
      const tableRows =
        order.OrderItems?.map((item) => [
          item.productName,
          item.quantity,
          parseFloat(item.price).toLocaleString(),
          (item.quantity * item.price).toLocaleString(),
        ]) || [];

      autoTable(doc, {
        startY: 250,
        head: [tableColumn],
        body: tableRows,
        theme: "grid",
        headStyles: {
          fillColor: accentColor,
          textColor: 255,
          fontStyle: "bold",
        },
        bodyStyles: { fillColor: lightColor, textColor: 0 },
        alternateRowStyles: { fillColor: [255, 255, 255] },
        margin: { left: 20, right: 20 },
      });

      // ---------- SUMMARY ----------
      let finalY = doc.lastAutoTable.finalY + 30;
      doc.setFontSize(12);
      doc.setFont("Nunito");
      doc.text(
        `Subtotal: ${parseFloat(order.subtotal || 0).toLocaleString()} Fbu`,
        20,
        finalY
      );
      finalY += 20;
      if (order.deliveryFee > 0) {
        doc.text(
          `Delivery Fee: ${parseFloat(order.deliveryFee).toLocaleString()} Fbu`,
          20,
          finalY
        );
        finalY += 20;
      }
      doc.text(
        `Total: ${parseFloat(order.totalPrice || 0).toLocaleString()} Fbu`,
        20,
        finalY
      );

      // ---------- FOOTER ----------
      const footerY = pageHeight - 60;

      doc.setFillColor(238, 238, 238);
      doc.rect(0, footerY - 20, pageWidth, 100, "F");

      doc.setFontSize(12);
      doc.setTextColor(80);

      doc.text("support@iblotec.com", 40, footerY);
      doc.text("+257 69316827", pageWidth / 2, footerY);
      doc.text(" iblotec.com", pageWidth - 100, footerY);

      doc.text(
        "Burundi - Bujumbura - Rohero - Avenue de la Revolution - Methodist Libre Church - Office: C6",
        40,
        footerY + 20,
        { maxWidth: pageWidth - 80 }
      );

      // ---------- WATERMARK AT BOTTOM RIGHT ----------
      logoImg.onload = () => {
        doc.addImage(
          logoImg,
          "PNG",
          pageWidth - 120,
          pageHeight - 200,
          100,
          100,
          "",
          "FAST",
          0.1
        );

        // ---------- SAVE ----------
        doc.save(`Invoice_${order.orderCode}.pdf`);
      };
    };
  };

  return (
    <div>
      <Navbar />
      <div className="order-detail-container">
        <div className="order-detail-card">
          <div className="order-header">
            <button
              onClick={() => navigate("/my-orders")}
              className="back-button"
            >
              <i className="fa-solid fa-arrow-left"></i> Back to Orders
            </button>
            <h1>
              <i className="fa-solid fa-receipt"></i> Order Details
            </h1>
            <p className="order-code">
              {" "}
              <span>CODE: </span> {order.orderCode}
            </p>
          </div>

          <div className="order-overview">
            <div className="status-section">
              <h3>Order Status</h3>
              <div
                className="status-badge"
                style={{ backgroundColor: getStatusColor(order.state) }}
              >
                {getStatusIcon(order.state)} {order.state?.toUpperCase()}
              </div>
            </div>

            <div className="info-section">
              <h3>Order Information</h3>
              <div className="info-grid">
                <div className="info-item">
                  <span className="label">Order Date:</span>
                  <span className="value">{formatDate(order.createdAt)}</span>
                </div>
                <div className="info-item">
                  <span className="label">Total Items:</span>
                  <span className="value">
                    {order.OrderItems?.reduce(
                      (total, item) => total + item.quantity,
                      0
                    ) || 0}
                  </span>
                </div>
                <div className="info-item">
                  <span className="label">Delivery:</span>
                  <span className="value">
                    {order.delivery ? "Yes (+5%)" : "No"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Proof Section */}
          <div className="payment-section">
            <h3>Payment Information</h3>
            {order.paymentProof ? (
              <div className="payment-proof">
                <h4>Payment Proof</h4>
                <div className="proof-status">
                  <span className={`status ${order.paymentStatus}`}>
                    <i
                      className={`fa-solid ${
                        order.paymentStatus === "verified"
                          ? "fa-check-circle"
                          : order.paymentStatus === "rejected"
                          ? "fa-times-circle"
                          : "fa-clock"
                      }`}
                    ></i>
                    {order.paymentStatus?.toUpperCase()}
                  </span>
                </div>
                <img
                  src={`${import.meta.env.VITE_API_URL}/${order.paymentProof}`}
                  alt="Payment proof"
                  className="proof-image"
                />
                <a
                  href={`${import.meta.env.VITE_API_URL}/${order.paymentProof}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="view-proof-link"
                >
                  <i className="fa-solid fa-expand"></i> View Full Size
                </a>

                {/* Show re-upload button if rejected */}
                {order.paymentStatus === "rejected" && (
                  <Link
                    to={`/upload-payment/${order.id}`}
                    className="upload-link"
                  >
                    <i className="fa-solid fa-upload"></i> Re-upload Payment
                    Proof
                  </Link>
                )}
              </div>
            ) : (
              <div className="no-payment-proof">
                <p>No payment proof uploaded yet.</p>
                {(order.state === "ready" ||
                  order.state === "pending" ||
                  order.state === "processing") && (
                  <Link
                    to={`/upload-payment/${order.id}`}
                    className="upload-link"
                  >
                    <i className="fa-solid fa-upload"></i> Upload Payment Proof
                  </Link>
                )}
              </div>
            )}
          </div>

          {/* Order Items */}
          <div className="items-section">
            <h3>Order Items</h3>
            <div className="items-list">
              {order.OrderItems?.map((item) => (
                <div key={item.id} className="order-item">
                  <img
                    src={`${import.meta.env.VITE_API_URL}/${item.productImage}`}
                    alt={item.productName}
                    className="item-image"
                  />
                  <div className="item-details">
                    <h4>{item.productName}</h4>
                    <p>Quantity: {item.quantity}</p>
                    <p>
                      Price: {parseFloat(item.price || 0).toLocaleString()} Fbu
                      each
                    </p>
                    <p className="item-total">
                      Total: {(item.quantity * item.price).toLocaleString()} Fbu
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Order Summary */}
          <div className="summary-section">
            <h3>Order Summary</h3>
            <div className="summary-grid">
              <div className="summary-item">
                <span>Subtotal:</span>
                <span>
                  {parseFloat(order.subtotal || 0).toLocaleString()} Fbu
                </span>
              </div>
              {order.deliveryFee > 0 && (
                <div className="summary-item">
                  <span>Delivery Fee:</span>
                  <span>
                    {parseFloat(order.deliveryFee || 0).toLocaleString()} Fbu
                  </span>
                </div>
              )}
              <div className="summary-item">
                <span>
                  <i className="fa-solid fa-money-bill-wave"></i> Total:
                </span>
                <span className="order-total">
                  {parseFloat(order.totalPrice || 0).toLocaleString()} Fbu
                </span>
              </div>
            </div>
          </div>

          <div className="action-buttons">
            <Link to="/my-orders" className="back-to-orders-btn">
              <i className="fa-solid fa-list"></i> Back to All Orders
            </Link>
            {!order.paymentProof &&
              (order.state === "ready" ||
                order.state === "pending" ||
                order.state === "processing") && (
                <Link
                  to={`/upload-payment/${order.id}`}
                  className="upload-proof-btn"
                >
                  <i className="fa-solid fa-upload"></i> Upload Payment Proof
                </Link>
              )}
            <button onClick={generatePDF} className="generate-pdf-btn">
              <i className="fa-solid fa-file-pdf"></i> Download Invoice
            </button>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default OrderDetail;
